import { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useDocumentTitle from "../../utils/useDocumentTitle";
import Button from "../../components/Button";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { useNotification } from "../../context/notification";
import PromptTemplateService from "../../services/PromptTemplateService";
import { PromptTemplateFilters, PromptTemplateTable } from "../../components/promptTemplate";
import { Pagination } from "../../components/Pagination";
import { TableConfig } from "../../config/Config";

export function PromptTemplateList(props) {
  useDocumentTitle(props.title);
  const { t } = useTranslation();
  const history = useHistory();
  const { addNotification } = useNotification();

  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [offset, setOffset] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage] = useState(TableConfig.resultPerPage);

  const [filters, setFilters] = useState({
    template_type: "",
    is_active: "",
    name: "",
  });

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page: offset,
        limit: perPage,
        ...(filters.template_type && { template_type: filters.template_type }),
        ...(filters.is_active !== "" && { is_active: filters.is_active }),
        ...(filters.name && { name: filters.name }),
      };
      const response = await PromptTemplateService.getAll(params);
      setTemplates(response.data || []);
      setTotalData(response.totalData || 0);
    } catch (error) {
      addNotification(
        t("error.couldNotGetSession") || "Failed to load templates",
        "error"
      );
    }
    setIsLoading(false);
  }, [offset, perPage, filters, addNotification, t]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    setOffset(1);
  }, [filters.template_type, filters.is_active, filters.name]);

  const handlePaginationChange = (event, value) => {
    setOffset(value);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleEdit = template => {
    history.push(`/prompt-templates/${template.id}/edit`);
  };

  const handleDelete = async id => {
    try {
      await PromptTemplateService.delete(id);
      addNotification("Template deleted successfully", "success");
      fetchTemplates();
    } catch (error) {
      addNotification("Failed to delete template", "error");
    }
  };

  const handleCreate = () => {
    history.push("/prompt-templates/create");
  };

  return (
    <DashboardLayout isDisableCampaignSelection={true}>
      <PageContent>
        <Section>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
            <SectionTitle>{t("promptTemplate.title") || "Prompt Templates"}</SectionTitle>
            <Button onClick={handleCreate}>
              {t("promptTemplate.create") || "Create Template"}
            </Button>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <PromptTemplateFilters
              templateType={filters.template_type}
              onTemplateTypeChange={val => handleFilterChange("template_type", val)}
              isActive={filters.is_active}
              onIsActiveChange={val => handleFilterChange("is_active", val)}
              searchKey={filters.name}
              onSearchChange={val => handleFilterChange("name", val)}
            />
          </div>

          <PromptTemplateTable
            templates={templates}
            isLoading={isLoading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />

          {totalData > perPage && (
            <Pagination
              count={Math.ceil(totalData / perPage)}
              page={offset - 1}
              onChange={handlePaginationChange}
            />
          )}
        </Section>
      </PageContent>
    </DashboardLayout>
  );
}